<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import {
  cabinetProfiles,
  cabinetSystems,
  estimateCabinetLayout,
  toCabinetConfigurationSnapshot,
  type CabinetLayoutEstimate,
  type CabinetSystemId
} from '@bf/domain'
import { useShowroomStore } from '../store/showroom'
import fcs500 from '../assets/cabinet-layouts/fcs-200-500x600.svg'
import fcs600 from '../assets/cabinet-layouts/fcs-200-600x800.svg'
import fcs800 from '../assets/cabinet-layouts/fcs-200-800x1000.svg'
import g3dm2500 from '../assets/cabinet-layouts/g3-dm2-500x600.svg'
import g3dm2600 from '../assets/cabinet-layouts/g3-dm2-600x800.svg'
import g3dm2800 from '../assets/cabinet-layouts/g3-dm2-800x1000.svg'
import pcs500 from '../assets/cabinet-layouts/pcs-200-500x600.svg'
import pcs600 from '../assets/cabinet-layouts/pcs-200-600x800.svg'
import pcs800 from '../assets/cabinet-layouts/pcs-200-800x1000.svg'

const store = useShowroomStore()
const pointCount = ref(50)
const selectedSystemId = ref<CabinetSystemId>('pcs-200')
const pointPresets = [50, 100, 180, 240]
const failedLayoutAssetId = ref('')
const photoPanelOpen = ref(false)
const uploadedPhotoUrls = ref<Record<string, string>>({})
const photoDatabaseName = 'bf-showroom-cabinet-photos'
const photoStoreName = 'photos'

const cabinetRenderUrls: Record<string, string> = {
  'cabinet-500x600-fcs-200': fcs500,
  'cabinet-600x800-fcs-200': fcs600,
  'cabinet-800x1000-fcs-200': fcs800,
  'cabinet-500x600-g3-dm2': g3dm2500,
  'cabinet-600x800-g3-dm2': g3dm2600,
  'cabinet-800x1000-g3-dm2': g3dm2800,
  'cabinet-500x600-pcs-200': pcs500,
  'cabinet-600x800-pcs-200': pcs600,
  'cabinet-800x1000-pcs-200': pcs800
}

const result = computed<{
  estimate: CabinetLayoutEstimate | null
  error: string
}>(() => {
  try {
    return {
      estimate: estimateCabinetLayout(Number(pointCount.value), selectedSystemId.value),
      error: ''
    }
  } catch (error) {
    return {
      estimate: null,
      error: error instanceof Error ? error.message : '无法生成机柜方案'
    }
  }
})

const estimate = computed(() => result.value.estimate)
const layoutAsset = computed(() =>
  estimate.value === null
    ? undefined
    : store.getCabinetLayoutAsset(estimate.value.layoutTemplateId)
)
const cabinetRenderUrl = computed(() =>
  estimate.value === null
    ? undefined
    : cabinetRenderUrls[estimate.value.layoutTemplateId]
)
const uploadedPhotoUrl = computed(() =>
  estimate.value === null
    ? undefined
    : uploadedPhotoUrls.value[estimate.value.layoutTemplateId]
)
const physicalPhotoUrl = computed(() => {
  if (uploadedPhotoUrl.value !== undefined) return uploadedPhotoUrl.value
  if (
    layoutAsset.value !== undefined &&
    failedLayoutAssetId.value !== layoutAsset.value.id
  ) {
    return store.mediaUrl(layoutAsset.value)
  }
  return undefined
})

function openPhotoDatabase() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(photoDatabaseName, 1)
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(photoStoreName)) {
        request.result.createObjectStore(photoStoreName)
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function savePhysicalPhoto(key: string, file: File) {
  const database = await openPhotoDatabase()
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(photoStoreName, 'readwrite')
    transaction.objectStore(photoStoreName).put(file, key)
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
  database.close()
}

async function loadPhysicalPhoto(key: string) {
  const database = await openPhotoDatabase()
  const blob = await new Promise<Blob | undefined>((resolve, reject) => {
    const request = database.transaction(photoStoreName).objectStore(photoStoreName).get(key)
    request.onsuccess = () => resolve(request.result as Blob | undefined)
    request.onerror = () => reject(request.error)
  })
  database.close()
  if (blob === undefined || uploadedPhotoUrls.value[key] !== undefined) return
  uploadedPhotoUrls.value = {
    ...uploadedPhotoUrls.value,
    [key]: URL.createObjectURL(blob)
  }
}

async function deletePhysicalPhoto(key: string) {
  const database = await openPhotoDatabase()
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(photoStoreName, 'readwrite')
    transaction.objectStore(photoStoreName).delete(key)
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
  database.close()
}

async function onPhysicalPhotoSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (file === undefined || estimate.value === null) return
  const key = estimate.value.layoutTemplateId
  const previous = uploadedPhotoUrls.value[key]
  if (previous !== undefined) URL.revokeObjectURL(previous)
  uploadedPhotoUrls.value = {
    ...uploadedPhotoUrls.value,
    [key]: URL.createObjectURL(file)
  }
  await savePhysicalPhoto(key, file)
  photoPanelOpen.value = true
  input.value = ''
}

async function removeUploadedPhoto() {
  if (estimate.value === null) return
  const key = estimate.value.layoutTemplateId
  const previous = uploadedPhotoUrls.value[key]
  if (previous === undefined) return
  URL.revokeObjectURL(previous)
  const next = { ...uploadedPhotoUrls.value }
  delete next[key]
  uploadedPhotoUrls.value = next
  await deletePhysicalPhoto(key)
}

function onPhysicalPhotoError() {
  if (layoutAsset.value !== undefined) {
    failedLayoutAssetId.value = layoutAsset.value.id
  }
}

onBeforeUnmount(() => {
  Object.values(uploadedPhotoUrls.value).forEach((url) => URL.revokeObjectURL(url))
})

watch(
  () => estimate.value?.layoutTemplateId,
  (key) => {
    if (key !== undefined) void loadPhysicalPhoto(key)
  },
  { immediate: true }
)

function addConfiguration() {
  if (estimate.value === null) return
  store.addCabinetConfiguration(toCabinetConfigurationSnapshot(estimate.value))
}
</script>

<template>
  <section class="generator-intro">
    <div>
      <p class="eyebrow">CABINET CONFIGURATION DEMO</p>
      <h2>输入点位，生成机柜沟通方案</h2>
      <p>
        输入物理 AI、AO、DI、DO 合计点位，选择控制系统。系统按 4 个规格档和
        9 套标准高清布局与 1 个特殊柜型示意给出机柜方案，并可保存到本次方案。
      </p>
    </div>
    <div class="generator-scope">
      <strong>3 + 1</strong>
      <span>3 种常用规格 + 1 种特殊柜型</span>
      <small>本地确定性生成，不依赖网络或运行时 AI</small>
    </div>
  </section>

  <div class="generator-layout">
    <aside class="generator-controls">
      <div class="control-block">
        <span class="control-step">01 / POINTS</span>
        <label for="cabinet-points">物理 I/O 点位合计</label>
        <div class="point-input">
          <input
            id="cabinet-points"
            v-model.number="pointCount"
            type="number"
            min="1"
            max="5000"
            step="1"
            inputmode="numeric"
            :aria-invalid="Boolean(result.error)"
            aria-describedby="cabinet-point-help"
          />
          <span>点</span>
        </div>
        <div class="point-presets" role="group" aria-label="常用点位">
          <button
            v-for="preset in pointPresets"
            :key="preset"
            type="button"
            :class="{ active: pointCount === preset }"
            :aria-pressed="pointCount === preset"
            @click="pointCount = preset"
          >
            {{ preset }}
          </button>
        </div>
        <small id="cabinet-point-help">
          点位不含 20% 柜内布局预留。允许输入 1–5000 的整数。
        </small>
      </div>

      <div class="control-block">
        <span class="control-step">02 / SYSTEM</span>
        <span class="control-label">控制系统</span>
        <div class="system-options">
          <label
            v-for="system in cabinetSystems"
            :key="system.id"
            :class="{ active: selectedSystemId === system.id }"
          >
            <input
              v-model="selectedSystemId"
              type="radio"
              name="cabinet-system"
              :value="system.id"
            />
            <span>
              <strong>{{ system.displayName }}</strong>
              <small>{{ system.controllerLabel }}</small>
            </span>
          </label>
        </div>
      </div>

      <div class="control-block">
        <span class="control-step">03 / CABINET</span>
        <span class="control-label">自动推荐规格</span>
        <div class="profile-list">
          <div
            v-for="profile in cabinetProfiles"
            :key="profile.id"
            :class="{ active: estimate?.cabinet.id === profile.id }"
          >
            <span>{{ profile.widthMm }} × {{ profile.heightMm }}</span>
            <small>{{ profile.pointRangeLabel }}</small>
          </div>
        </div>
      </div>
    </aside>

    <section v-if="estimate" class="cabinet-result" aria-live="polite">
      <header class="result-header">
        <div>
          <p class="eyebrow">GENERATED LAYOUT</p>
          <h3>{{ estimate.cabinet.title }}</h3>
          <p>
            {{ estimate.system.displayName }} · {{ estimate.pointCount }} 点 ·
            模板 {{ estimate.layoutTemplateId }}
          </p>
        </div>
        <span
          class="review-state"
          :class="{ warning: estimate.requiresEngineeringReview }"
        >
          {{ estimate.requiresEngineeringReview ? '需工程复核' : '演示范围内' }}
        </span>
      </header>

      <div class="cabinet-stage">
        <div
          v-if="cabinetRenderUrl"
          class="cabinet-render-preview"
        >
          <img
            :key="estimate.layoutTemplateId"
            :src="cabinetRenderUrl"
            :alt="`${estimate.system.displayName} ${estimate.cabinet.title}高清机柜布局图`"
          />
          <small>高清矢量布局 · 可无损缩放查看设备与端子细节</small>
          <div class="cabinet-photo-actions">
            <button class="button button-secondary" type="button" @click="photoPanelOpen = true">
              实物照片
            </button>
            <label class="button button-ghost cabinet-photo-upload">
              添加实物照片
              <input type="file" accept="image/*" @change="onPhysicalPhotoSelected" />
            </label>
          </div>
        </div>
        <div
          v-else
          class="cabinet-shell"
          :class="`cabinet-${estimate.cabinet.id}`"
          role="img"
          :aria-label="`${estimate.cabinet.title}布局示意`"
        >
          <div class="cabinet-nameplate">
            <span>BF / BA</span>
            <small>{{ estimate.system.displayName }}</small>
          </div>
          <div
            v-for="rail in estimate.rails"
            :key="rail.id"
            class="cabinet-rail"
          >
            <span class="rail-label">{{ rail.label }}</span>
            <div class="rail-track">
              <div
                v-for="item in rail.items"
                :key="item.id"
                class="rail-item"
                :data-kind="item.kind"
              >
                <span>{{ item.label }}</span>
                <b v-if="item.quantity > 1">×{{ item.quantity }}</b>
              </div>
            </div>
          </div>
        </div>

        <dl class="result-metrics">
          <div>
            <dt>推荐柜型</dt>
            <dd>{{ estimate.cabinet.widthMm }} × {{ estimate.cabinet.heightMm }} mm</dd>
          </div>
          <div>
            <dt>模拟 IO 模块</dt>
            <dd>{{ estimate.estimatedIoModules }} 组</dd>
          </div>
          <div>
            <dt>模拟端子组</dt>
            <dd>{{ estimate.estimatedTerminalGroups }} 组</dd>
          </div>
          <div>
            <dt>布局预留</dt>
            <dd>{{ estimate.reservePercent }}%</dd>
          </div>
        </dl>
      </div>

      <div class="generator-warnings">
        <p v-for="warning in estimate.warnings" :key="warning">{{ warning }}</p>
      </div>

      <footer class="generator-actions">
        <button class="button button-primary" type="button" @click="addConfiguration">
          ＋ 保存到本次方案
        </button>
        <RouterLink
          class="button button-secondary"
          :to="{ name: 'item', params: { id: estimate.system.contentEntityId } }"
        >
          查看系统资料
        </RouterLink>
        <RouterLink class="button button-ghost" :to="{ name: 'catalog', params: { section: 'hardware' } }">
          硬件产品库
        </RouterLink>
        <RouterLink class="button button-ghost" :to="{ name: 'basket' }">
          查看方案篮
        </RouterLink>
      </footer>
    </section>

    <section v-else class="generator-error" role="alert">
      <strong>暂时无法生成</strong>
      <p>{{ result.error }}</p>
    </section>
  </div>

  <p class="engineering-disclaimer">
    本功能用于客户现场沟通。生成内容是演示模拟，不是设计图、制造图、接线图、
    施工图或正式 BOM；柜深、散热、电源、隔离、回路和器件选型由工程师复核。
  </p>

  <div v-if="photoPanelOpen" class="cabinet-photo-dialog" role="dialog" aria-modal="true" aria-label="机柜实物照片">
    <button class="cabinet-photo-backdrop" type="button" aria-label="关闭实物照片" @click="photoPanelOpen = false"></button>
    <section>
      <header>
        <div>
          <p class="eyebrow">PHYSICAL CABINET PHOTO</p>
          <h3>{{ estimate?.system.displayName }} · {{ estimate?.cabinet.title }}</h3>
        </div>
        <button type="button" aria-label="关闭" @click="photoPanelOpen = false">×</button>
      </header>
      <div v-if="physicalPhotoUrl" class="cabinet-physical-photo">
        <img :src="physicalPhotoUrl" alt="当前机柜实物照片" @error="onPhysicalPhotoError" />
      </div>
      <div v-else class="cabinet-photo-empty">
        <strong>尚未添加实物照片</strong>
        <p>高清布局图仍作为默认展示，实物照片仅在此窗口查看。</p>
      </div>
      <footer>
        <label class="button button-primary cabinet-photo-upload">
          {{ physicalPhotoUrl ? '更换实物照片' : '添加实物照片' }}
          <input type="file" accept="image/*" @change="onPhysicalPhotoSelected" />
        </label>
        <button v-if="uploadedPhotoUrl" class="button button-ghost" type="button" @click="removeUploadedPhoto">
          移除人工照片
        </button>
      </footer>
    </section>
  </div>
</template>
